import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPinned, Search, LocateFixed, X, Check, History } from 'lucide-react';
import { toast } from 'sonner';
import { geoSearch, geoReverse, getRecent, addRecent } from '../lib/geocode';

let resolver = null;
// opens the "choose place" step; resolves { lat, lon, zoom, addr } or null (cancel)
export const pickPlace = (kind) => new Promise((res) => { resolver?.(null); resolver = res; window.dispatchEvent(new CustomEvent('giss-pick-place', { detail: kind })); });
const ADDR_KEY = 'giss_place_addr';
// "46.66, 11.00" / "46,66; 11,00" / "46.66 11.00"
const parseCoords = (s) => {
  const m = String(s).trim().match(/^(-?\d{1,2}(?:[.,]\d+)?)\s*(?:[;\s]|,\s)\s*(-?\d{1,3}(?:[.,]\d+)?)$/);
  if (!m) return null;
  const lat = +m[1].replace(',', '.'), lon = +m[2].replace(',', '.');
  return Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? { lat, lon } : null;
};

// floating panel over the live map: search address / own position / coordinates / recent places, then "create here" at the crosshair
export default function PlacePicker({ t, lang, mapRef }) {
  const [kind, setKind] = useState(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [useAddr, setUseAddr] = useState(() => localStorage.getItem(ADDR_KEY) !== '0');
  const last = useRef(null);
  const go = (lat, lon, name) => { mapRef.current?.setView(lat, lon, 19); last.current = name ? { lat, lon, name } : null; setHits([]); };
  const locate = (quiet) => navigator.geolocation?.getCurrentPosition((p) => go(p.coords.latitude, p.coords.longitude), () => !quiet && toast.error(t.plNoGps), { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 });
  useEffect(() => {
    const h = (e) => {
      setKind(e.detail || 'project'); setQ(''); setHits([]); last.current = null;
      document.querySelector('[data-testid="map-wrapper"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (window.matchMedia('(pointer: coarse)').matches) locate(true);
    };
    window.addEventListener('giss-pick-place', h);
    return () => window.removeEventListener('giss-pick-place', h);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!kind) return null;
  const close = (v) => { setKind(null); resolver?.(v); resolver = null; };
  const search = async (e) => {
    e?.preventDefault();
    const c = parseCoords(q);
    if (c) return go(c.lat, c.lon);
    if (!q.trim()) return undefined;
    setBusy(true);
    try { const r = await geoSearch(q, lang); setHits(r); if (!r.length) toast.error(t.plNone); } catch { toast.error(t.plOffline); }
    return setBusy(false);
  };
  const ok = () => {
    const c = mapRef.current.getCenter();
    const near = last.current && Math.abs(last.current.lat - c.lat) < 0.0002 && Math.abs(last.current.lon - c.lng) < 0.0002;
    const addr = useAddr && near ? last.current.name : null;
    if (addr) addRecent({ lat: c.lat, lon: c.lng, name: addr });
    // without a fresh search hit the address is looked up in the background after creating
    const later = useAddr && !addr ? geoReverse(c.lat, c.lng, lang).then((a) => { if (a) addRecent({ lat: c.lat, lon: c.lng, name: a }); return a; }) : null;
    close({ lat: c.lat, lon: c.lng, zoom: c.zoom, addr, later });
  };
  const recent = getRecent();
  return createPortal(
    <div className="place-picker" data-testid="place-picker">
      <div className="pl-head"><MapPinned size={18} /><b>{t['plTitle_' + kind] || t.plTitle_project}</b><button className="icon-btn" data-testid="place-cancel-x" onClick={() => close(null)}><X size={16} /></button></div>
      <p className="muted small-hint">{t.plHint}</p>
      <form className="pl-search" onSubmit={search}>
        <input data-testid="place-search-input" value={q} placeholder={t.plSearch} onChange={(e) => setQ(e.target.value)} />
        <button className="btn btn-dark btn-sm" data-testid="place-search-btn" disabled={busy}><Search size={14} /></button>
        <button type="button" className="btn btn-outline btn-sm" data-testid="place-gps-btn" title={t.plGps} onClick={() => locate(false)}><LocateFixed size={14} /></button>
      </form>
      {hits.length > 0 && <div className="pl-hits" data-testid="place-hits">{hits.map((h, i) => <button key={i} data-testid={`place-hit-${i}`} onClick={() => go(h.lat, h.lon, h.name)}>{h.name}</button>)}</div>}
      {!hits.length && recent.length > 0 && <div className="pl-recent" data-testid="place-recent"><History size={13} />{recent.map((h, i) => <button key={i} className="chip" data-testid={`place-recent-${i}`} onClick={() => go(h.lat, h.lon, h.name)}>{h.name.split(',')[0]}</button>)}</div>}
      <label className="chk-row"><input type="checkbox" data-testid="place-addr-check" checked={useAddr} onChange={(e) => { setUseAddr(e.target.checked); localStorage.setItem(ADDR_KEY, e.target.checked ? '1' : '0'); }} /> {t.plUseAddr}</label>
      <div className="row">
        <button className="btn btn-green" data-testid="place-ok-btn" onClick={ok}><Check size={16} /> {t['plOk_' + kind] || t.plOk_project}</button>
        <button className="btn btn-outline btn-sm" data-testid="place-cancel-btn" onClick={() => close(null)}>{t.btnCancel}</button>
      </div>
    </div>,
    document.body,
  );
}
